import { useState, useEffect } from 'react';
import { X, Calendar as CalendarIcon, Clock } from 'lucide-react';
import { AlertModal } from './AlertModal';

interface ScheduleMessagingModalProps {
  batchId: string; // Used for API call identification
  batchName: string;
  contactCount: number;
  onClose: () => void;
  onSchedule: (data: ScheduleData) => Promise<void>;
  isReschedule?: boolean;
  currentScheduledFor?: string | null; // ISO timestamp of current scheduled time
  currentIntroTag?: string; // Current intro tag value
}

export interface ScheduleData {
  sendTiming: 'now' | 'later';
  scheduledDate?: string;
  scheduledTime?: string;
  timezoneOffset?: number;
  introTag?: string;
}

export function ScheduleMessagingModal({
  batchId: _batchId, // Passed from parent but not used directly in this component
  batchName,
  contactCount,
  onClose,
  onSchedule,
  isReschedule = false,
  currentScheduledFor,
  currentIntroTag,
}: ScheduleMessagingModalProps) {
  const [sendTiming, setSendTiming] = useState<'now' | 'later'>('now');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [introTag, setIntroTag] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alertModal, setAlertModal] = useState<{ isOpen: boolean; title: string; message: string }>({ isOpen: false, title: '', message: '' });

  // Pre-populate form fields when in reschedule mode
  useEffect(() => {
    if (isReschedule && currentScheduledFor) {
      // Ensure the timestamp is treated as UTC by adding 'Z' if not present
      const utcTimestamp = currentScheduledFor.endsWith('Z') ? currentScheduledFor : currentScheduledFor + 'Z';
      
      // Parse the UTC timestamp - browser automatically converts to local time
      const scheduledDate = new Date(utcTimestamp);
      
      // Format date as YYYY-MM-DD for HTML date input (in local timezone)
      const year = scheduledDate.getFullYear();
      const month = String(scheduledDate.getMonth() + 1).padStart(2, '0');
      const day = String(scheduledDate.getDate()).padStart(2, '0');
      const dateString = `${year}-${month}-${day}`;
      
      // Format time as HH:MM for HTML time input (in local timezone)
      const hours = String(scheduledDate.getHours()).padStart(2, '0');
      const minutes = String(scheduledDate.getMinutes()).padStart(2, '0');
      const timeString = `${hours}:${minutes}`;
      
      setSendTiming('later');
      setScheduledDate(dateString);
      setScheduledTime(timeString);
      setIntroTag(currentIntroTag || '');
    }
  }, [isReschedule, currentScheduledFor, currentIntroTag]);

  const showAlert = (title: string, message: string) => {
    setAlertModal({ isOpen: true, title, message });
  };

  const closeAlert = () => {
    setAlertModal({ isOpen: false, title: '', message: '' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (sendTiming === 'later' && (!scheduledDate || !scheduledTime)) {
      showAlert('Validation Error', 'Please select both date and time for scheduled messaging');
      return;
    }

    setIsSubmitting(true);
    try {
      // Get user's timezone offset in minutes from UTC
      // Positive values are west of UTC (e.g., EST = 300 minutes = UTC-5)
      const timezoneOffset = new Date().getTimezoneOffset();
      
      await onSchedule({
        sendTiming,
        scheduledDate: sendTiming === 'later' ? scheduledDate : undefined,
        scheduledTime: sendTiming === 'later' ? scheduledTime : undefined,
        timezoneOffset,
        introTag: introTag.trim() !== '' ? introTag : undefined,
      });
      onClose();
    } catch (error) {
      console.error('Failed to schedule messaging:', error);
      showAlert('Error', 'Failed to schedule messaging. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] flex flex-col">
        {/* Header - stays fixed at top */}
        <div className="flex-shrink-0 flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">{isReschedule ? 'Reschedule Messaging' : 'Schedule for Messaging'}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable content area */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-900">
              <strong>{contactCount}</strong> contacts from batch <strong>{batchName}</strong> will be {isReschedule ? 'rescheduled' : 'tagged'} for messaging.
            </p>
          </div>

          <div className="space-y-4">
            <label className="block text-sm font-medium text-gray-700">
              When to send messages
            </label>
            
            <div className="space-y-3">
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="radio"
                  name="sendTiming"
                  value="now"
                  checked={sendTiming === 'now'}
                  onChange={(e) => setSendTiming(e.target.value as 'now' | 'later')}
                  className="w-4 h-4 text-blue-600"
                />
                <span className="text-sm text-gray-900">Send ASAP</span>
              </label>

              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="radio"
                  name="sendTiming"
                  value="later"
                  checked={sendTiming === 'later'}
                  onChange={(e) => setSendTiming(e.target.value as 'now' | 'later')}
                  className="w-4 h-4 text-blue-600"
                />
                <span className="text-sm text-gray-900">Do Not Send Before</span>
              </label>
            </div>
          </div>

          {sendTiming === 'later' && (
            <div className="space-y-4 pl-7">
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <CalendarIcon className="w-4 h-4" />
                  Date
                </label>
                <input
                  type="date"
                  value={scheduledDate}
                  onChange={(e) => setScheduledDate(e.target.value)}
                  min={(() => {
                    const today = new Date();
                    const year = today.getFullYear();
                    const month = String(today.getMonth() + 1).padStart(2, '0');
                    const day = String(today.getDate()).padStart(2, '0');
                    return `${year}-${month}-${day}`;
                  })()}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required={sendTiming === 'later'}
                />
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <Clock className="w-4 h-4" />
                  Time
                </label>
                <input
                  type="time"
                  value={scheduledTime}
                  onChange={(e) => setScheduledTime(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required={sendTiming === 'later'}
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Intro Tag (Optional)
            </label>
            <input
              type="text"
              value={introTag}
              onChange={(e) => setIntroTag(e.target.value)}
              placeholder="Enter intro tag or leave blank for default workflow handling"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              Add an intro tag here to help route messages properly in workflows
            </p>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (isReschedule ? 'Rescheduling...' : 'Scheduling...') : (isReschedule ? 'Reschedule Messaging' : 'Schedule Messaging')}
            </button>
          </div>
        </form>
      </div>

      <AlertModal
        isOpen={alertModal.isOpen}
        title={alertModal.title}
        message={alertModal.message}
        onClose={closeAlert}
      />
    </div>
  );
}
